import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  apiCheckVendorMediaPurchasePayment,
  apiCreateVendorMediaPurchase,
  apiGetMyVendorMediaUsage,
} from "../api";
import socket from "../utilities/socket";

const fmtBytes = (bytes = 0) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
};

const fmtMoney = (amount, currency = "UGX") =>
  `${currency === "UGX" ? "USh" : currency} ${Number(amount || 0).toLocaleString()}`;

const statusVariant = (status = "") => {
  if (status === "APPROVED") return "success";
  if (status === "PAYMENT_SUCCESSFUL") return "info";
  if (status === "PAYMENT_FAILED" || status === "REJECTED") return "danger";
  if (status === "PAYMENT_INIT_FAILED") return "warning";
  return "secondary";
};

const VendorMediaUsage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [data, setData] = useState({ usage: null, purchases: [], recentAssets: [] });
  const [selectedPackage, setSelectedPackage] = useState("");
  const [phone, setPhone] = useState("");

  const usage = data.usage || {};
  const packages = usage.packages || [];
  const usedPct = useMemo(() => {
    const total = Number(usage.totalAllowance) || 1;
    return Math.min(100, Math.round(((Number(usage.activeImages) || 0) / total) * 100));
  }, [usage.activeImages, usage.totalAllowance]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGetMyVendorMediaUsage();
      setData(res.data.data || {});
      const first = res.data.data?.usage?.packages?.[0]?.code;
      if (first) setSelectedPackage((prev) => prev || first);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load media usage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const vendorId = data.usage?.vendorId;
    if (!vendorId) return undefined;
    const join = () => socket.emit("joinVendorMediaRoom", vendorId);
    const refresh = (event = {}) => {
      if (!event.vendorId || String(event.vendorId) === String(vendorId)) load();
    };
    join();
    socket.on("connect", join);
    socket.on("vendorMedia:updated", refresh);
    return () => {
      socket.off("connect", join);
      socket.off("vendorMedia:updated", refresh);
      socket.emit("leaveVendorMediaRoom", vendorId);
    };
  }, [data.usage?.vendorId]);

  const buy = async () => {
    if (!selectedPackage) return toast.error("Choose an image pack");
    if (!phone.trim()) return toast.error("Enter the MTN number to charge");
    setSubmitting(true);
    try {
      const res = await apiCreateVendorMediaPurchase({
        packageCode: selectedPackage,
        phone,
      });
      const purchase = res.data.data?.purchase;
      if (purchase?.status === "PAYMENT_INIT_FAILED") {
        toast.info("Purchase created. MTN payment could not be started here, so Lusuku can approve it manually.");
      } else {
        toast.success("MTN payment request sent. Approve the PIN prompt on your phone.");
      }
      setPhone("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create purchase");
    } finally {
      setSubmitting(false);
    }
  };

  const checkPayment = async (purchaseId) => {
    setCheckingId(purchaseId);
    try {
      await apiCheckVendorMediaPurchasePayment(purchaseId);
      toast.success("Payment status refreshed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to check payment");
    } finally {
      setCheckingId(null);
    }
  };

  if (loading) {
    return (
      <div className="content d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                <div>
                  <h3 className="mb-1">Media & Storage</h3>
                  <div className="text-muted">
                    Your first{" "}
                    <strong>{usage.includedImages ?? "…"} active images are FREE</strong>. Extra
                    images are billed through Lusuku image packs.
                  </div>
                </div>
                <Badge bg={usage.uploadAllowed ? "success" : "danger"} className="mt-2">
                  {usage.uploadAllowed ? "Uploads allowed" : "Uploads blocked"}
                </Badge>
              </div>

              <Row className="g-3">
                <Col md="3"><Metric title="Free images" value={usage.includedImages ?? 0} /></Col>
                <Col md="3"><Metric title="Images used" value={`${usage.activeImages || 0} / ${usage.totalAllowance || 0}`} /></Col>
                <Col md="3"><Metric title="Remaining" value={usage.remainingImages || 0} /></Col>
                <Col md="3"><Metric title="Extra purchased" value={usage.approvedExtraImages || 0} /></Col>
              </Row>
              <div className="mt-2 text-muted small">Storage used: {fmtBytes(usage.storageBytes)}</div>
              <div className="mt-3 text-muted">
                Enforcement: <strong>{usage.enforcementMode || "observe"}</strong>
              </div>
              {usage.reservedUploads > 0 && (
                <div className="text-muted mt-2">
                  {usage.reservedUploads} image slot{usage.reservedUploads === 1 ? " is" : "s are"} temporarily reserved by an upload in progress.
                </div>
              )}

              <div className="mt-4">
                <div className="d-flex justify-content-between mb-1">
                  <strong>Image allowance</strong>
                  <span>{usedPct}%</span>
                </div>
                <ProgressBar now={usedPct} variant={usedPct >= 100 ? "danger" : usedPct >= 80 ? "warning" : "success"} />
                {usage.outOfImages && (
                  <div className="alert alert-warning mt-3 mb-0">
                    You have used your included image allowance. Buy an image pack to keep adding product photos and extra angles.
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col lg="7">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <h4>Buy more image space</h4>
              <Row className="g-3 mt-1">
                {packages.map((pkg) => (
                  <Col md="4" key={pkg.code}>
                    <button
                      type="button"
                      onClick={() => setSelectedPackage(pkg.code)}
                      className="w-100 text-left"
                      style={{
                        border: selectedPackage === pkg.code ? "2px solid #84b026" : "1px solid #dbe3ea",
                        borderRadius: 8,
                        background: "#fff",
                        padding: 14,
                        minHeight: 132,
                      }}
                    >
                      <strong>{pkg.label}</strong>
                      <div style={{ fontSize: 24, fontWeight: 800, color: "#0f5132" }}>{pkg.imageCredits}</div>
                      <div className="text-muted">extra images</div>
                      <div className="mt-2">{fmtMoney(pkg.amount, pkg.currency)}</div>
                    </button>
                  </Col>
                ))}
              </Row>
              <Form.Group className="mt-3">
                <Form.Label>MTN Mobile Money number</Form.Label>
                <Form.Control
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 0771234567"
                />
                <Form.Text muted>Payment is made to Lusuku. Approve the MTN PIN prompt to unlock the pack.</Form.Text>
              </Form.Group>
              <Button className="mt-3" variant="success" onClick={buy} disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : "Buy image pack"}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg="5">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <h4>Recent purchases</h4>
              {data.purchases?.length ? (
                <Table responsive size="sm" className="mt-3">
                  <tbody>
                    {data.purchases.map((p) => (
                      <tr key={p._id}>
                        <td>
                          <strong>{p.packageCode}</strong>
                          <div className="text-muted">{p.imageCredits} images · {fmtMoney(p.amount, p.currency)}</div>
                          <div className="text-muted small">{p.mtnReferenceId || "Awaiting MTN reference"}</div>
                        </td>
                        <td><Badge bg={statusVariant(p.status)}>{p.status}</Badge></td>
                        <td className="text-right">
                          {["PENDING_PAYMENT", "PAYMENT_SUCCESSFUL"].includes(p.status) && (
                            <Button size="sm" variant="outline-primary" onClick={() => checkPayment(p._id)} disabled={checkingId === p._id}>
                              {checkingId === p._id ? "Checking..." : "Check"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-muted mt-3">No image-pack purchases yet.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md="12">
          <Card className="shadow-sm">
            <Card.Body>
              <h4>Recent images</h4>
              {data.recentAssets?.length ? (
                <Row className="g-3 mt-1">
                  {data.recentAssets.map((asset) => (
                    <Col xs="6" md="3" lg="2" key={asset._id}>
                      <div style={{ border: "1px solid #e6edf3", borderRadius: 8, overflow: "hidden", height: "100%" }}>
                        <img
                          src={asset.cloudinaryUrl}
                          alt={asset.assetPurpose || "Vendor image"}
                          style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }}
                        />
                        <div style={{ padding: 8 }}>
                          <strong className="d-block text-truncate">{asset.assetPurpose || "image"}</strong>
                          <small className="text-muted">{fmtBytes(asset.bytes)}</small>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-muted mt-3">No quota-tracked images yet.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const Metric = ({ title, value }) => (
  <div style={{ border: "1px solid #e6edf3", borderRadius: 8, padding: 14, background: "#fbfdff" }}>
    <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#15324a" }}>{value}</div>
  </div>
);

export default VendorMediaUsage;
